import { DateTime } from "luxon";
import moment from "moment";

import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { CallCenterCallReason } from "@/Common/Models/CallCenter/CallCenterCallReason";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PatientCommunicationsListItem } from "@/Patient/Models/History/PatientCommunicationsListItem";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientHistoryCommunicationsHandler extends BaseOperationHandler {
    private debounceTimeoutReference: number;

    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.History.Communications_LoadPatientCommunication, this.LoadPatientCommunications);
        this.RegisterOperation(LocalPatientOperations.History.Communications_AddNewCommunication, this.OpenNewCommunicationPopup);
        this.RegisterOperation(LocalPatientOperations.History.Communications_CancelAddNewCommunications, this.CloseNewCommunicationPopup);
        this.RegisterOperation(LocalPatientOperations.History.Communications_StatusChange, this.StatusChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_NoteTextChange, this.NotesChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_ClinicianAdded, this.ClinicianAdded);
        this.RegisterOperation(LocalPatientOperations.History.Communications_ClinicianCleared, this.ClinicianCleared);
        this.RegisterOperation(LocalPatientOperations.History.Communications_CallDateChange, this.CallDateChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_CallTimeStartChange, this.CallTimeStartChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_CallTimeEndChange, this.CallTimeEndChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_CallReasonChange, this.CallReasonChange);
        this.RegisterOperation(LocalPatientOperations.History.Communications_OkAddNewCommunications, this.CreateNewCommunication);
        this.RegisterOperation(LocalPatientOperations.History.Communications_OkAddNewCallCommunications, this.CreateNewCallCommunication);
        this.RegisterOperation(LocalPatientOperations.History.Communications_EditCallCommunications, this.EditCallCommunicationPopup);
        this.RegisterOperation(LocalPatientOperations.History.Communications_DeleteCommunications, this.DeleteCommunicationRecord);
        this.RegisterOperation(LocalPatientOperations.History.Communications_OkEditCallCommunications, this.EditCallCommunication);
        this.RegisterOperation(LocalPatientOperations.History.Communications_onPortalStatusClicked, this.onPortalStatusClick);
        this.RegisterOperation(LocalPatientOperations.History.Communications_onCloseSendToPortalPopupClick, this.onCloseSendToPortalPopupClick);
        this.RegisterOperation(LocalPatientOperations.History.Communications_onSharedItemCheck, this.onSharedItemCheck);
        this.RegisterOperation(LocalPatientOperations.History.Communications_onSendToPortalClick, this.onSendToPortalClick);
        this.RegisterOperation(LocalPatientOperations.History.Communications_PortalStatusRevoke, this.communicationPortalStatusRevoked);
        this.RegisterOperation(LocalPatientOperations.History.OnPortalCommunicationMessageChecked, this.onPortalCommunicationMessageChecked);

        this.debounceTimeoutReference = -1;
    }

    private onSendToPortalClick(request: LocalEvent, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        let portalStatusModel: IPortalLinkedItemModel = communicationModel.get("PortalLinkedItemModel");

        let msgText = request.Get<string>("messageText");
        let objectId: string = !IsNullOrWhiteSpace(portalStatusModel.get("LinkedItemId")) ? portalStatusModel.get("LinkedItemId") : "";

        let portalMessages: SharedItemMessage[] = [];
        if (portalStatusModel != null) {
            portalMessages = portalStatusModel
                .get("LinkedItemMessages")
                .toArray()
                .filter((x: SharedItemMessage) => !!x.IsSelected && !x.IsDisabled);
        }

        let sharedItemId: string = "";

        if (portalStatusModel.get("LinkedItemModel") != null) {
            let itemModel = portalStatusModel.get("LinkedItemModel");
            sharedItemId = itemModel.getSharedItemId(); // May be null
        }

        let hasTextMessage = (msgText != null && msgText != undefined && msgText.trim().length > 0);

        // create new message if user has entered text in msg txtbox
        if (hasTextMessage) {
            portalMessages.push(new SharedItemMessage({
                Text: msgText,
                Status: PortalSyncStatus.Unsent,
                LastStatusDate: moment().toDate(),
                SentByContactId: sessionStore.getCurrentEmployee().ContactId,
                CreatedByUserId: sessionStore.userId,
                DateCreated: moment().toDate(),
                IsSelected: false,
                IsDisabled: false
            }));
        }

        let patient = store.PatientModels[request.PatientId];

        store.ActionsHistory.sendHistorySharedItemToPortal(
            objectId,
            sharedItemId,
            null,
            PortalLinkedItemType.CommunicationHistory,
            sessionStore.getCurrentEmployee().ContactId,
            patient.IndividualResponse.Contact_id,
            sessionStore.userId,
            portalMessages,
            false,
            false,
            false
        );

        portalStatusModel = portalStatusModel.set("IsSending", true);
        communicationModel = communicationModel.set("PortalLinkedItemModel", portalStatusModel);

        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private onSharedItemCheck(request: LocalEvent, store: PatientStore): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        let checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let model = communicationsModel.get("PortalLinkedItemModel");
            model = PatientHelper.handlePortalItemCheck(model, checkedItem);
            communicationsModel = communicationsModel.set("PortalLinkedItemModel", model);
            store.PatientHistoryCommunicationsModels[request.PatientId] = communicationsModel;
        }
    }

    private onCloseSendToPortalPopupClick(request: LocalEvent, store: PatientStore): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", false);
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationsModel;
    }

    private onPortalStatusClick(request: LocalEvent, store: PatientStore): void {
        let communicationsModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        let item = request.Get<PatientCommunicationsListItem>("item");
        let linkedItemModel = communicationsModel.get("PortalLinkedItemModel");

        linkedItemModel = linkedItemModel.set("LinkedItemId", item.Model.Id);
        linkedItemModel = linkedItemModel.set("LinkedItemModel", item.Model);
        linkedItemModel = linkedItemModel.set("SharedItem", item.Model.SharedItem);
        linkedItemModel = linkedItemModel.set("IsSending", false);
        linkedItemModel = linkedItemModel.set("DefaultBody", item.Model.Note);
        linkedItemModel = linkedItemModel.set("RegistrationStatus", communicationsModel.get("RegistrationStatus"));
        linkedItemModel = linkedItemModel.set("PatientContactId", request.PatientId);

        if (item.Model.SharedItem && !IsNullOrWhiteSpace(item.Model.SharedItem.SharedItemId)) {
            communicationsModel = communicationsModel.set("PortalLinkedItemModel", linkedItemModel);
            store.ActionsHistory.LoadPortalSharedItem(item.Model.getSharedItemId(), PortalLinkedItemType.CommunicationHistory);
        }
        else {
            linkedItemModel = linkedItemModel.set("LinkedItemMessages", Immutable.List([]));
            linkedItemModel = linkedItemModel.set("LinkedItemType", PortalLinkedItemType.CommunicationHistory);

            communicationsModel = communicationsModel.set("PortalLinkedItemModel", linkedItemModel);
            communicationsModel = communicationsModel.set("IsSendToPatientPortalPopupOpen", true);
        }

        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationsModel;
    }

    private LoadPatientCommunications(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientUIModels[request.PatientId];
        const patientId = uiModel.get("PatientContactFacetId");
        store.ActionsHistory.LoadPatientCommunications(patientId);
    }

    private OpenNewCommunicationPopup(request: LocalEvent, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("ShowNewCommunicationPopup", true);

        if (cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            const clearDate = this.getCurrentTimeMinutesAccuracy().toJSDate();
            const employeeContactFacetId = cacheStore.GetCurrentEmployee().ContactFacet_id;
            communicationModel = communicationModel
                .set("SelectedClinician", cacheStore.GetCliniciansPersonsByContactFacetId(employeeContactFacetId))
                .set("Note", "")
                .set("CallStartDateTime", clearDate)
                .set("CallEndTime", clearDate.addMinutes(5))
                .set("CallReasons", null);
        }

        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;

        overlayStore.showOverlay("global-overlay");
    }

    private CloseNewCommunicationPopup(request: LocalEvent, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("ShowNewCommunicationPopup", false);
        if (cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            const clearDate = new Date();
            communicationModel = communicationModel
                .set("Note", "")
                .set("PatientCommunicationHistoryId", null)
                .set("SelectedClinician", null)
                .set("ShowEditMode", false)
                .set("CallStartDateTime", clearDate)
                .set("CallEndTime", clearDate.addMinutes(5))
                .set("CallReasons", null);
        }
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;

        overlayStore.hideOverlay("global-overlay");
    }

    private debounceAction(action: () => void): void {
        if (this.debounceTimeoutReference != -1) {
            clearTimeout(this.debounceTimeoutReference);
            this.debounceTimeoutReference = -1;
        }

        this.debounceTimeoutReference = window.setTimeout(action, 250);
    }

    private CreateNewCommunication(request: LocalEvent, store: PatientStore): void {
        const communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;

        this.debounceAction(() => {
            store.ActionsHistory.CreateNewCommunication(
                store.PatientModels[request.PatientId].InactivableResponse.Id,
                communicationModel
            );
        });
    }

    private CreateNewCallCommunication(request: LocalEvent, store: PatientStore): void {
        const communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;

        this.debounceAction(() => {
            store.ActionsHistory.CreateNewCallCommunication(
                store.PatientModels[request.PatientId].InactivableResponse.Id,
                communicationModel
            );
        });
    }

    private DeleteCommunicationRecord(request: LocalEvent, store: PatientStore): void {
        const communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];

        this.debounceAction(() => {
            store.ActionsHistory.DeleteCallCommunicationRecord(communicationModel);
        });
    }

    private EditCallCommunicationPopup(request: LocalEvent, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        const item = request.Get<PatientCommunicationsListItem>("item");

        communicationModel = communicationModel
            .set("ShowNewCommunicationPopup", true)
            .set("PatientCommunicationHistoryId", item.Model.Id)
            .set("ShowEditMode", true)
            .set("SelectedStatusId", item.Model.FollowUpId)
            .set("Note", item.Model.Note)
            .set("CallStartDateTime", new Date(item.Model.CallStart))
            .set("CallEndTime", item.Model.CallEnd ? new Date(item.Model.CallEnd) : null)
            .set("CallReasons", Immutable.List(item.Model.CallReasons))
            .set("FromEmployee", item.Model.FromEmployee);

        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
        overlayStore.showOverlay("global-overlay");
    }

    private EditCallCommunication(request: LocalEvent, store: PatientStore): void {
        const communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];

        if (this.debounceTimeoutReference != -1) {
            clearTimeout(this.debounceTimeoutReference);
            this.debounceTimeoutReference = -1;
        }

        this.debounceTimeoutReference = window.setTimeout(
            () => {
                store.ActionsHistory.EditCallCommunication(
                    communicationModel
                );
            },
            250
        );
    }

    private StatusChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("SelectedStatusId", val);
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private NotesChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("Note", val);
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private ClinicianAdded(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<PersonDropdownItem>("value");
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("SelectedClinician", val.Item);
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private ClinicianCleared(request: LocalEvent, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("SelectedClinician", null);
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private UpdateCommunicationModelDates(patientId: string, startDateTime: Date, endDateTime: Date, store: PatientStore): void {
        let communicationModel = store.PatientHistoryCommunicationsModels[patientId];
        communicationModel = communicationModel
            .set("CallStartDateTime", startDateTime)
            .set("CallEndTime", endDateTime);
        store.PatientHistoryCommunicationsModels[patientId] = communicationModel;
    }

    /**
     * Increments the end date to the following day for calls between
     * 11PM and 1AM to account for the next day.
     * @param startDateTime incoming start date time value as a moment
     * @param endDateTime incoming end date time value as a moment
     * @returns
     */
    private extendEndDateForOvernightSpan(startDateTime: DateTime, endDateTime: DateTime): DateTime {
        if (startDateTime.hour >= 23 && (endDateTime.hour < 1 || (endDateTime.hour === 1 && endDateTime.minute === 0))) {
            return endDateTime.plus({ hours: 24 });
        }
        return endDateTime;
    }

    private CallDateChange(request: LocalEvent, store: PatientStore): void {
        const startDate = LuxonDateTimeParser.parse(request.Get<Date>("value") ?? this.getCurrentTimeMinutesAccuracy()).toLocal();
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];

        const callStartDateTime = LuxonDateTimeParser.parse(communicationModel.get("CallStartDateTime")).toLocal();
        const callEndTime = LuxonDateTimeParser.parse(communicationModel.get("CallEndTime")).toLocal();

        const newStartTime = startDate.set({ hour: callStartDateTime.hour, minute: callStartDateTime.minute });
        let newEndTime = startDate.set({ hour: callEndTime.hour, minute: callEndTime.minute });
        newEndTime = this.extendEndDateForOvernightSpan(newStartTime, newEndTime);

        this.UpdateCommunicationModelDates(request.PatientId, newStartTime.toJSDate(), newEndTime.toJSDate(), store);
    }

    private CallTimeChangeHelper(request: LocalEvent, store: PatientStore, isStartTimeChange: boolean): void {
        const timeValue = LuxonDateTimeParser.parse(request.Get<Date>("value") ?? this.getCurrentTimeMinutesAccuracy()).toLocal();
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];

        let currentStartDate = LuxonDateTimeParser.parse(communicationModel.get("CallStartDateTime")).toLocal();
        let currentEndDate = LuxonDateTimeParser.parse(communicationModel.get("CallEndTime")).toLocal();

        if (isStartTimeChange) {
            currentStartDate = currentStartDate.set({ hour: timeValue.hour, minute: timeValue.minute });
            currentEndDate = currentEndDate.set({ year: currentStartDate.year, month: currentStartDate.month, day: currentStartDate.day });
        }
        else {
            currentEndDate = currentStartDate.set({ hour: timeValue.hour, minute: timeValue.minute });
        }

        currentEndDate = this.extendEndDateForOvernightSpan(currentStartDate, currentEndDate);
        this.UpdateCommunicationModelDates(request.PatientId, currentStartDate.toJSDate(), currentEndDate.toJSDate(), store);
    }

    private getCurrentTimeMinutesAccuracy(): DateTime {
        return DateTime.now().set({ second: 0, millisecond: 0 });
    }

    private CallTimeStartChange(request: LocalEvent, store: PatientStore): void {
        this.CallTimeChangeHelper(request, store, true);
    }

    private CallTimeEndChange(request: LocalEvent, store: PatientStore): void {
        this.CallTimeChangeHelper(request, store, false);
    }

    private CallReasonChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<CallCenterCallReason[]>("value");
        let communicationModel = store.PatientHistoryCommunicationsModels[request.PatientId];
        communicationModel = communicationModel.set("CallReasons", Immutable.List(val));
        store.PatientHistoryCommunicationsModels[request.PatientId] = communicationModel;
    }

    private communicationPortalStatusRevoked(request: LocalEvent, store: PatientStore): void {
        let model = store.PatientHistoryCommunicationsModels[request.PatientId];
        let portalStatusModel = model.get("PortalLinkedItemModel");
        let sharedItemId = portalStatusModel.get("LinkedItemModel").getSharedItemId();
        let communicationId = portalStatusModel.get("LinkedItemId");

        if (!IsNullOrWhiteSpace(sharedItemId)) {
            let messageIdsToRevoke = PortalHelper.getSharedMessageIdsToRevoke(portalStatusModel);
            store.ActionsHistory.revokeHistorySharedItem(sharedItemId, messageIdsToRevoke, sessionStore.userId, PortalLinkedItemType.CommunicationHistory, communicationId, true);
        }
    }

    private onPortalCommunicationMessageChecked(request: LocalEvent, store: PatientStore): void {
        const checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let communicaionModel = store.PatientHistoryCommunicationsModels[request.PatientId];
            let model = communicaionModel.get("PortalLinkedItemModel");

            model = PatientHelper.handlePortalItemCheck(model, checkedItem);

            communicaionModel = communicaionModel.set("PortalLinkedItemModel", model);

            store.PatientHistoryCommunicationsModels[request.PatientId] = communicaionModel;
        }
    }
}

export { PatientHistoryCommunicationsHandler };
